import axios from "axios";
import { CheckToken } from "../login/AccessToken";
import { getAuthHeaders } from "../ipd-billing/constants";

const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;

/** Lab bench service group (pathology). Radiology = 1. */
export const LAB_SERVICE_GROUP_ID = 10;
export const RADIOLOGY_SERVICE_GROUP_ID = 1;

function authHeaders(includeJson = false) {
  CheckToken();
  return getAuthHeaders(includeJson);
}

function qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

function assertSuccess(data) {
  if (!data || data.status !== "Success") {
    const err = new Error(data?.message || data?.error || "Request failed");
    err.response = { data };
    throw err;
  }
  return data;
}

const READ_CACHE_MS = 4000;
const assignmentsInflight = new Map();
const assignmentsCache = new Map();
const eligibleInflight = new Map();
const eligibleCache = new Map();
const myAssignmentsInflight = new Map();

function dedupeInFlight(store, key, factory) {
  if (store.has(key)) return store.get(key);
  const promise = Promise.resolve()
    .then(factory)
    .finally(() => store.delete(key));
  store.set(key, promise);
  return promise;
}

function readCacheHit(cache, key) {
  const hit = cache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at > READ_CACHE_MS) {
    cache.delete(key);
    return null;
  }
  return hit.data;
}

function writeCache(cache, key, data) {
  cache.set(key, { at: Date.now(), data });
}

function isNumericId(value) {
  if (value == null || value === "") return false;
  const n = Number(value);
  return Number.isFinite(n) && String(value).trim() === String(n);
}

export function getLoggedInDiagnosticDoctorPk() {
  const pk = sessionStorage.getItem("diagnostic_doctor_pk");
  if (isNumericId(pk)) return Number(pk);
  const legacy = sessionStorage.getItem("diagnostic_doctor_id");
  if (isNumericId(legacy)) return Number(legacy);
  return null;
}

export function getLoggedInDiagnosticDoctorCode() {
  const code =
    sessionStorage.getItem("diagnostic_doctor_code") ||
    sessionStorage.getItem("diagnostic_doctor_id") ||
    "";
  if (!code || isNumericId(code)) return "";
  return String(code);
}

export function rememberQueueDoctorIdentity(item) {
  if (!item) return;
  if (isNumericId(item.diagnostic_doctor_id)) {
    sessionStorage.setItem(
      "diagnostic_doctor_pk",
      String(item.diagnostic_doctor_id)
    );
  }
  if (item.diagnostic_doctor_code) {
    sessionStorage.setItem(
      "diagnostic_doctor_code",
      String(item.diagnostic_doctor_code)
    );
  }
}

/** Match the logged-in doctor in detail `assignments[]` via int PK or UUID. */
export function isMyDiagnosticAssignment(assignment, queuePk) {
  if (!assignment) return false;
  const assignPk = Number(assignment.diagnostic_doctor_id);
  if (queuePk != null && isNumericId(queuePk) && assignPk === Number(queuePk)) {
    return true;
  }
  const myPk = getLoggedInDiagnosticDoctorPk();
  if (myPk != null && Number.isFinite(assignPk) && assignPk === myPk) {
    return true;
  }
  const myCode = getLoggedInDiagnosticDoctorCode().toLowerCase();
  const assignCode = String(
    assignment.diagnostic_doctor_code ||
      (!isNumericId(assignment.diagnostic_doctor_id)
        ? assignment.diagnostic_doctor_id
        : "") ||
      ""
  ).toLowerCase();
  return Boolean(myCode && assignCode && myCode === assignCode);
}

export function getDiagnosticDoctorRoleType() {
  const stored = String(
    sessionStorage.getItem("diagnostic_doctor_role_type") ||
      sessionStorage.getItem("roleType") ||
      ""
  ).toLowerCase();
  if (stored === "radiologist" || stored === "pathologist") return stored;
  const group = Number(sessionStorage.getItem("service_group_id"));
  if (group === RADIOLOGY_SERVICE_GROUP_ID) return "radiologist";
  if (group === LAB_SERVICE_GROUP_ID) return "pathologist";
  return stored;
}

export function getServiceGroupBadge(roleType) {
  const group = Number(sessionStorage.getItem("service_group_id"));
  if (group === LAB_SERVICE_GROUP_ID) return "Laboratory";
  if (group === RADIOLOGY_SERVICE_GROUP_ID) return "Radiology";
  return roleType === "radiologist" ? "Radiology" : "Laboratory";
}

export function getDiagnosticDoctorCopy(roleType) {
  const role = roleType || getDiagnosticDoctorRoleType();
  const isRad = role === "radiologist";
  return {
    role,
    isRadiologist: isRad,
    pageTitle: isRad ? "Radiology Report Review" : "Pathology Report Review",
    domain: isRad ? "Radiology" : "Pathology",
    groupBadge: getServiceGroupBadge(role),
    navLabel: isRad ? "Radiology Dashboard" : "Pathology Dashboard",
    emptyPending: isRad
      ? "No radiology reports pending review"
      : "No pathology reports pending review",
    emptyGeneric: "No reports assigned",
    sign: isRad ? "Sign Radiology Report" : "Sign Pathology Report",
    publish: isRad ? "Publish Radiology Report" : "Publish Pathology Report",
  };
}

export const QUEUE_TABS = [
  { id: "assigned", label: "Pending" },
  { id: "signed", label: "Signed" },
  { id: "rejected", label: "Rejected" },
  { id: "all", label: "All" },
];

export function classifyDiagnosticError(err) {
  const msg = String(err?.message || err?.response?.data?.message || "");
  const lower = msg.toLowerCase();
  if (lower.includes("invalid user access")) return "logout";
  if (lower.includes("does not have access")) return "forbidden";
  if (lower.includes("not assigned")) return "not_assigned";
  if (lower.includes("signature is required")) return "signature";
  if (lower.includes("already signed") || lower.includes("already rejected")) {
    return "stale";
  }
  if (
    lower.includes("cannot be changed") ||
    lower.includes("already published")
  ) {
    return "readonly";
  }
  return "generic";
}

/**
 * Mode 1 (recommended): patient_lab_test_id + role_type → doctors, assignments, classification.
 * Mode 2 (list only): classification_name + service_group_id + role_type → doctors only.
 */
export async function getEligibleDiagnosticDoctors({
  patientLabTestId,
  roleType = "pathologist",
  classificationName,
  serviceGroupId,
} = {}) {
  const params = { role_type: roleType };

  const pltId = Number(patientLabTestId);
  if (Number.isFinite(pltId) && pltId > 0) {
    params.patient_lab_test_id = pltId;
  } else if (classificationName && serviceGroupId != null) {
    params.classification_name = classificationName;
    params.service_group_id = serviceGroupId;
  } else {
    throw new Error("patient_lab_test_id is required");
  }

  const cacheKey = JSON.stringify(params);
  const cached = readCacheHit(eligibleCache, cacheKey);
  if (cached) return cached;

  return dedupeInFlight(eligibleInflight, cacheKey, async () => {
    const res = await axios.get(
      `${BASE}get_eligible_diagnostic_doctors/${qs(params)}`,
      { headers: authHeaders() }
    );
    const data = assertSuccess(res.data);
    writeCache(eligibleCache, cacheKey, data);
    return data;
  });
}

export async function assignDiagnosticDoctors(patientLabTestId, diagnosticDoctorIds) {
  const res = await axios.post(
    `${BASE}assign_diagnostic_doctors/`,
    {
      patient_lab_test_id: Number(patientLabTestId),
      diagnostic_doctor_ids: diagnosticDoctorIds.map(Number),
    },
    { headers: authHeaders(true) }
  );
  return assertSuccess(res.data);
}

export function invalidateDiagnosticReportAssignments(patientLabTestId) {
  const pltId = Number(patientLabTestId);
  if (Number.isFinite(pltId) && pltId > 0) {
    assignmentsCache.delete(pltId);
  }
}

export async function getDiagnosticReportAssignments(patientLabTestId) {
  const pltId = Number(patientLabTestId);
  if (!Number.isFinite(pltId) || pltId <= 0) {
    throw new Error("patient_lab_test_id is required");
  }
  const cached = readCacheHit(assignmentsCache, pltId);
  if (cached) return cached;

  return dedupeInFlight(assignmentsInflight, pltId, async () => {
    const res = await axios.get(
      `${BASE}get_diagnostic_report_assignments/${qs({
        patient_lab_test_id: pltId,
      })}`,
      { headers: authHeaders() }
    );
    const data = assertSuccess(res.data);
    writeCache(assignmentsCache, pltId, data);
    return data;
  });
}

export async function getMyDiagnosticAssignments(status, { skipGlobalLoader } = {}) {
  const cacheKey = status == null || status === "" ? "__all__" : String(status);
  return dedupeInFlight(myAssignmentsInflight, cacheKey, async () => {
    const res = await axios.get(
      `${BASE}get_my_diagnostic_assignments/${qs({ status })}`,
      { headers: authHeaders(), skipGlobalLoader: !!skipGlobalLoader }
    );
    return assertSuccess(res.data);
  });
}

export async function signDiagnosticReport(patientLabTestId) {
  const res = await axios.post(
    `${BASE}sign_diagnostic_report/`,
    { patient_lab_test_id: Number(patientLabTestId) },
    { headers: authHeaders(true) }
  );
  return assertSuccess(res.data);
}

export async function rejectDiagnosticReport(patientLabTestId, rejectionReason) {
  const res = await axios.post(
    `${BASE}reject_diagnostic_report/`,
    {
      patient_lab_test_id: Number(patientLabTestId),
      rejection_reason: String(rejectionReason || "").trim(),
    },
    { headers: authHeaders(true) }
  );
  return assertSuccess(res.data);
}

export async function publishDiagnosticReport(patientLabTestId) {
  const res = await axios.post(
    `${BASE}publish_diagnostic_report/`,
    { patient_lab_test_id: Number(patientLabTestId) },
    { headers: authHeaders(true) }
  );
  return assertSuccess(res.data);
}

export const PUBLISH_STATUS_META = {
  pending: { label: "Pending", color: "#e67e22", bg: "#fef5e7" },
  published: { label: "Published", color: "#27ae60", bg: "#eafaf1" },
  rejected: { label: "Rejected", color: "#c0392b", bg: "#fdedec" },
};

export const ASSIGNMENT_STATUS_META = {
  assigned: { label: "Assigned", color: "#2980b9", bg: "#ebf5fb" },
  signed: { label: "Signed", color: "#27ae60", bg: "#eafaf1" },
  rejected: { label: "Rejected", color: "#c0392b", bg: "#fdedec" },
};

export function publishRuleHint(rule) {
  if (rule === "ALL_ASSIGNED") {
    return "All assigned doctors must sign before publish.";
  }
  return "Any one assigned doctor can publish after signing.";
}

function toDataUrl(value) {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  if (/^data:image\/(png|jpeg|jpg|webp|gif);base64,/i.test(trimmed)) {
    return trimmed;
  }
  if (/^data:.*;base64,/i.test(trimmed)) {
    return trimmed.replace(/^data:[^;]+;base64,/i, "data:image/png;base64,");
  }
  const mime = trimmed.startsWith("iVBORw0KGgo") ? "png" : "jpeg";
  if (/^[A-Za-z0-9+/=\r\n]+$/.test(trimmed)) {
    return `data:image/${mime};base64,${trimmed.replace(/\s+/g, "")}`;
  }
  return trimmed.startsWith("data:") ? trimmed : "";
}

/** Pull a printable image from the shapes admin / assignment APIs send. */
export function extractDiagnosticSignatureImage(value) {
  if (!value) return "";
  if (typeof value === "string") return toDataUrl(value);
  if (Array.isArray(value)) {
    return extractDiagnosticSignatureImage(value[0]);
  }
  if (typeof value === "object") {
    return toDataUrl(
      value.base64 ||
        value.content ||
        value.data ||
        value.doctor_signature ||
        value.signature
    );
  }
  return "";
}

function doctorDisplayName(source) {
  if (!source) return "";
  const joined = [source.salutation, source.first_name, source.last_name]
    .map((p) => String(p || "").trim())
    .filter(Boolean)
    .join(" ");
  return (
    String(
      source.name ||
        source.diagnostic_doctor_name ||
        source.doctor_name ||
        source.full_name ||
        joined
    ).trim() || ""
  );
}

function signatureFromSource(source) {
  if (!source) return "";
  return extractDiagnosticSignatureImage(
    source.doctor_signature ||
      source.signature ||
      source.signed_signature ||
      source.signature_image ||
      source.signature_base64
  );
}

function sessionSignature() {
  return extractDiagnosticSignatureImage(
    sessionStorage.getItem("diagnostic_doctor_signature")
  );
}

function sessionDoctorName() {
  return String(sessionStorage.getItem("diagnostic_doctor_name") || "").trim();
}

// `includeSigned` is for the reviewing doctor's own preview: their signature
// shows the moment they sign. Every other viewer waits for publish.
export function signedDiagnosticSignatoriesFromBundle(
  bundle,
  { includeSigned = false } = {}
) {
  const assignments = Array.isArray(bundle?.assignments)
    ? bundle.assignments
    : Array.isArray(bundle?.data?.assignments)
      ? bundle.data.assignments
      : [];
  // A signature only goes on the report once it is published — signing alone
  // leaves it a draft that every viewer should still see unsigned.
  const publishStatus = String(
    bundle?.report?.report_publish_status ||
      bundle?.data?.report?.report_publish_status ||
      ""
  ).toLowerCase();
  if (publishStatus !== "published" && !includeSigned) return [];
  const signed = assignments.filter(
    (a) => String(a?.status || "").toLowerCase() === "signed"
  );
  const mySig = sessionSignature();
  const myName = sessionDoctorName();
  return signed.map((assignment) => {
    let image = signatureFromSource(assignment);
    if (!image && isMyDiagnosticAssignment(assignment)) {
      image = mySig;
    }
    const name =
      doctorDisplayName(assignment) ||
      (isMyDiagnosticAssignment(assignment) ? myName : "") ||
      "Signed";
    const role = String(
      assignment.role_type ||
        bundle?.role_type ||
        ""
    ).toLowerCase();
    return {
      id:
        assignment.diagnostic_doctor_id ??
        assignment.id ??
        name,
      name,
      image,
      signedAt: assignment.signed_at || assignment.updated_at || "",
      roleLabel:
        role === "radiologist"
          ? "Radiologist"
          : role === "pathologist"
            ? "Pathologist"
            : "Consultant",
    };
  });
}

export async function fetchSignedDiagnosticSignatories(
  patientLabTestId,
  { roleType } = {}
) {
  const review = await fetchDiagnosticReview(patientLabTestId, { roleType });
  return review.signatories;
}

export function diagnosticRejectionFromBundle(bundle) {
  const report = bundle?.report || {};
  const publish = String(report.report_publish_status || "").toLowerCase();
  const rejectedAssignments = (bundle?.assignments || []).filter(
    (a) => String(a?.status || "").toLowerCase() === "rejected"
  );
  const reasons = rejectedAssignments
    .map((a) => String(a.rejection_reason || a.reason || "").trim())
    .filter(Boolean);
  const reportReason = String(
    report.rejection_reason || report.report_rejection_reason || ""
  ).trim();
  return {
    rejected:
      publish === "rejected" ||
      rejectedAssignments.length > 0 ||
      Boolean(reportReason),
    reason: reasons[0] || reportReason || "",
  };
}

export async function fetchDiagnosticReview(
  patientLabTestId,
  { roleType } = {}
) {
  const empty = {
    signatories: [],
    rejection: { rejected: false, reason: "" },
  };
  const pltId = Number(patientLabTestId);
  if (!Number.isFinite(pltId) || pltId <= 0) return empty;
  try {
    const res = await getDiagnosticReportAssignments(pltId);
    const bundle = res.data || {};
    const list = signedDiagnosticSignatoriesFromBundle(bundle);
    const publishStatus = String(
      bundle?.report?.report_publish_status || "",
    ).toLowerCase();
    const rejection = diagnosticRejectionFromBundle(bundle);
    const assignmentList = Array.isArray(bundle?.assignments)
      ? bundle.assignments
      : [];
    // Sent to a diagnostic doctor and still with them: an active assignment
    // (assigned or signed) on a report that is neither published nor rejected.
    const submitted =
      publishStatus !== "published" &&
      !rejection.rejected &&
      assignmentList.some((a) =>
        ["assigned", "signed"].includes(String(a?.status || "").toLowerCase())
      );
    return {
      signatories: list,
      rejection,
      submitted,
      published: publishStatus === "published",
      reportPublishStatus: publishStatus,
    };
  } catch (err) {
    return empty;
  }
}

/** After a diagnostic doctor publishes, the lab/radiology board should read Verified. */
export async function markLabOrderVerified(report) {
  if (!report) return;
  const status = "Verified";
  const admissionId = report.admission_id;
  const orderNo = report.order_no;
  const patientType = String(report.patient_type || "").toUpperCase();
  const isIpd = patientType === "IPD" || (admissionId && patientType !== "OPD");
  if (isIpd && admissionId && orderNo) {
    const res = await axios.post(
      `${BASE}update_lab_test_status/`,
      {
        admission_id: String(admissionId),
        order_no: orderNo,
        status,
      },
      { headers: authHeaders(true) }
    );
    if (res?.data?.is_valid === false || res?.data?.status === "Failed") {
      throw new Error(res.data?.message || "Could not set Verified");
    }
    return;
  }
  const labTestId =
    report.lab_test_id || report.patient_lab_test_id || orderNo;
  if (!labTestId) return;
  const res = await axios.post(
    `${BASE}change_patient_lab_test_status/`,
    JSON.stringify({
      lab_tests: [{ lab_test_id: String(labTestId), status }],
    }),
    { headers: authHeaders(true) }
  );
  if (res?.data?.is_valid === false || res?.data?.status === "Failed") {
    throw new Error(res.data?.message || "Could not set Verified");
  }
}

// "Submitted for Review": the report was sent to a diagnostic doctor and is
// still waiting on them. report_publish_status is NOT a usable signal for it:
// "pending" also comes back for a finalized report nobody has been assigned
// yet (radiology orders), and the bench's own "revised after rejection" lands
// on "pending" too. So this tracks the session's submit / revise actions by
// order number; after a reload the modals ask the assignments instead
// (useDiagnosticReview -> submittedByPltId).
const submittedForReview = new Set();
const revisedAfterRejection = new Set();

export function markReviewSubmitted(orderNo) {
  const key = String(orderNo || "");
  if (!key) return;
  submittedForReview.add(key);
  revisedAfterRejection.delete(key);
}

export function markReviewRevised(orderNo) {
  const key = String(orderNo || "");
  if (!key) return;
  revisedAfterRejection.add(key);
  submittedForReview.delete(key);
}

export function isReviewSubmitted(order) {
  const key = String(order?.orderNo || "");
  const publish = String(order?.reportPublishStatus || "").toLowerCase();
  if (publish === "published" || order?.status === "Verified") return false;
  if (publish === "rejected" || String(order?.rejectionReason || "").trim()) {
    return false;
  }
  if (revisedAfterRejection.has(key)) return false;
  return submittedForReview.has(key);
}

export async function resolveReportSignatories({
  signatories,
  patientLabTestId,
  extraIds = [],
  roleType,
} = {}) {
  const ids = [patientLabTestId, ...extraIds]
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
  const unique = [...new Set(ids)];

  if (Array.isArray(signatories) && signatories.length > 0) {
    const ready = signatories.filter((s) => s && (s.image || s.name));
    // Names alone are not enough — re-read the assignments for the image.
    if (ready.some((s) => s.image) || unique.length === 0) return ready;
  }

  if (unique.length === 0) return [];
  const groups = await Promise.all(
    unique.map((id) => fetchSignedDiagnosticSignatories(id, { roleType }))
  );
  const byKey = new Map();
  groups.flat().forEach((s) => {
    if (!s) return;
    const key = s.id != null ? `id:${s.id}` : `name:${s.name}`;
    const prev = byKey.get(key);
    if (!prev || (!prev.image && s.image)) byKey.set(key, s);
  });
  return [...byKey.values()];
}

export function formatSignatoryNames(signatories) {
  const names = (signatories || [])
    .map((s) => String(s?.name || "").trim())
    .filter(Boolean);
  return names.join(", ");
}

